/**
 * Attribution marketing — mémorisation de la PREMIÈRE touche.
 *
 * Règle : la première touche gagne. Un visiteur qui arrive par une campagne,
 * repart, revient trois jours plus tard en direct puis s'inscrit reste attribué
 * à la campagne. Écraser à chaque visite créditerait le trafic direct de tout
 * le travail fait par les campagnes — c'est le biais classique du "last touch"
 * et il rendrait les chiffres inexploitables pour décider où investir.
 *
 * Durée de vie : 30 jours. Au-delà, on considère que le lien entre l'arrivée
 * et l'inscription n'est plus démontrable et on repart d'une page blanche.
 */

const KEY = 'zk_attribution';
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'];

/* Tout accès au stockage local est enveloppé : navigation privée, cookies
   bloqués ou quota plein lèvent une exception. Une mesure marketing ne doit
   jamais faire tomber une page. */
function safeGet() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.saved_at) return null;
    if (Date.now() - data.saved_at > TTL_MS) {
      try { localStorage.removeItem(KEY); } catch { /* ignoré */ }
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function safeSet(data) {
  try { localStorage.setItem(KEY, JSON.stringify(data)); } catch { /* ignoré */ }
}

/** Paramètres de campagne présents dans l'URL COURANTE (peut être vide). */
export function readUtmFromUrl(search = window.location.search) {
  const params = new URLSearchParams(search || '');
  const out = {};
  UTM_KEYS.forEach((k) => {
    const v = (params.get(k) || '').trim();
    if (v) out[k] = v.slice(0, 100);
  });
  return out;
}

/**
 * À appeler au tout premier rendu de n'importe quelle page.
 * N'écrase jamais une attribution déjà enregistrée.
 * Retourne l'attribution en vigueur (nouvelle ou existante), ou null.
 */
export function captureAttribution() {
  const existing = safeGet();
  if (existing) return existing;

  const utm = readUtmFromUrl();

  /* Un referrer externe est une origine en soi : arriver depuis un forum sans
     paramètre UTM reste une information, et on la perdrait en n'enregistrant
     que les campagnes taguées. Le referrer interne (même hôte) n'en est pas
     une : c'est juste une navigation entre nos propres pages. */
  let referrer = '';
  try {
    const ref = document.referrer || '';
    if (ref && new URL(ref).host !== window.location.host) referrer = ref;
  } catch { referrer = ''; }

  const hasSignal = Object.keys(utm).length > 0 || referrer;
  if (!hasSignal) return null; // arrivée directe : rien à mémoriser

  const data = {
    utm_source: utm.utm_source || null,
    utm_medium: utm.utm_medium || null,
    utm_campaign: utm.utm_campaign || null,
    referrer: referrer ? referrer.slice(0, 500) : null,
    landing_page: (window.location.pathname + window.location.search).slice(0, 500),
    first_seen_at: new Date().toISOString(),
    saved_at: Date.now(),
  };
  safeSet(data);
  return data;
}

/**
 * Attribution à joindre au payload d'inscription, ou null.
 * `saved_at` est un détail d'implémentation du TTL : il n'est pas envoyé.
 */
export function getAttribution() {
  const data = safeGet();
  if (!data) return null;
  const { saved_at, ...payload } = data; // eslint-disable-line no-unused-vars
  return payload;
}
